import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { statusReportKeys } from "@/lib/query";

export interface StatusReportDraft {
  draft: string;
  subject: string;
  /** Combined, deduplicated list of all recipients across both teams. */
  recipients: string[];
  teams: {
    postDelivery: string[];
    deliveryCompliance: string[];
  };
}

export function useStatusReportDraft(enabled: boolean) {
  return useQuery({
    queryKey: statusReportKeys.draft,
    queryFn: async (): Promise<StatusReportDraft> => {
      const res = await fetch("/api/reports/status-report");
      if (!res.ok) throw new Error("Failed to generate status report");
      const json = await res.json();
      return json.data;
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useSendStatusReport() {
  return useMutation({
    mutationFn: async (data: { subject: string; body: string; slackChannelKeys: string[] }) => {
      const res = await fetch("/api/reports/status-report/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const json = await res.json();
        throw new Error((json as { error?: string }).error ?? "Failed to send report");
      }
      const json = await res.json();
      return (json as { data: { sent: number; suppressed: number; slackChannels: number } }).data;
    },
    onSuccess: (data) => {
      const parts: string[] = [];
      if (data.sent > 0) parts.push(`${data.sent} email recipient${data.sent !== 1 ? "s" : ""}`);
      if (data.slackChannels > 0)
        parts.push(`${data.slackChannels} Slack channel${data.slackChannels !== 1 ? "s" : ""}`);
      toast.success(`Report sent to ${parts.length > 0 ? parts.join(" and ") : "0 recipients"}`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to send report");
    },
  });
}
